import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { config } from '../config';
import { TrendingUp, TrendingDown, Activity, DollarSign, Play, Square, RefreshCw, Settings2, ChevronRight, Target, LogOut, User, Shield, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import SignalPanel from '../components/SignalPanel';
import TradeHistory from '../components/TradeHistory';
import TradeStats from '../components/TradeStats';
import ConnectionStatus from '../components/ConnectionStatus';
import { InstallPrompt } from '../components/InstallPrompt';
import { UpdatePrompt } from '../components/UpdatePrompt';
import SignalNotificationPopup from '../components/SignalNotificationPopup';
import GuestAuthModal from '../components/GuestAuthModal';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const Dashboard = () => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [accountType, setAccountType] = useState('demo');
  const [botRunning, setBotRunning] = useState(false);
  const [botMode, setBotMode] = useState('manual');
  const [selectedAsset, setSelectedAsset] = useState('EURUSD_OTC');
  const [tradeAmount, setTradeAmount] = useState(10);
  const [assets, setAssets] = useState([]);
  const [signals, setSignals] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [stats24h, setStats24h] = useState(null);
  const [pollStats, setPollStats] = useState(null);
  const [scannerRunning, setScannerRunning] = useState(false);
  const [scannerStatus, setScannerStatus] = useState(null);
  const [recentSignals, setRecentSignals] = useState([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState(60); // Default 1 minute
  
  // Manual Signal Generator states
  const [manualPairs, setManualPairs] = useState([]);
  const [selectedManualPair, setSelectedManualPair] = useState('EURUSD_OTC');
  const [generatingSignal, setGeneratingSignal] = useState(false);
  const [lastManualSignalResult, setLastManualSignalResult] = useState(null);
  
  // Guest Authentication states
  const [guestUser, setGuestUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);  // Check if admin is logged in
  
  // Check if user is authenticated (admin or approved guest)
  const isAuthenticated = () => {
    return isAdmin || (guestUser && guestUser.status === 'approved');
  };
  
  const [sendToBot, setSendToBot] = useState(() => {
    const saved = localStorage.getItem('sendToBot');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [sendToChannel, setSendToChannel] = useState(() => {
    const saved = localStorage.getItem('sendToChannel');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [telegramChannelId, setTelegramChannelId] = useState(() => {
    return localStorage.getItem('telegramChannelId') || '';
  });
  const [autoReportResult, setAutoReportResult] = useState(() => {
    const saved = localStorage.getItem('autoReportResult');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [autoReportMartingale, setAutoReportMartingale] = useState(() => {
    const saved = localStorage.getItem('autoReportMartingale');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [pendingManualResult, setPendingManualResult] = useState(false); // Has pending manual result
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('pushNotificationsEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [autoStartScanner, setAutoStartScanner] = useState(() => {
    const saved = localStorage.getItem('autoStartScanner');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('autoRefreshEnabled');
    return saved !== null ? JSON.parse(saved) : true; // Default enabled
  });
  const [nextRefreshTime, setNextRefreshTime] = useState(null);
  
  // Signal Notification Popup state
  const [showSignalPopup, setShowSignalPopup] = useState(false);
  const [currentSignalDetails, setCurrentSignalDetails] = useState(null);
  const [lastSignalId, setLastSignalId] = useState(null); // Track to prevent duplicate popups
  const [dashboardNotificationsEnabled, setDashboardNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('dashboardNotificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true; // Default enabled
  });

  // Save dashboard notification setting
  useEffect(() => {
    localStorage.setItem('dashboardNotificationsEnabled', JSON.stringify(dashboardNotificationsEnabled));
  }, [dashboardNotificationsEnabled]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('sendToBot', JSON.stringify(sendToBot));
  }, [sendToBot]);

  useEffect(() => {
    localStorage.setItem('sendToChannel', JSON.stringify(sendToChannel));
  }, [sendToChannel]);

  useEffect(() => {
    localStorage.setItem('telegramChannelId', telegramChannelId);
  }, [telegramChannelId]);

  useEffect(() => {
    localStorage.setItem('autoReportResult', JSON.stringify(autoReportResult));
  }, [autoReportResult]);

  useEffect(() => {
    localStorage.setItem('autoReportMartingale', JSON.stringify(autoReportMartingale));
  }, [autoReportMartingale]);

  useEffect(() => {
    localStorage.setItem('pushNotificationsEnabled', JSON.stringify(pushNotificationsEnabled));
  }, [pushNotificationsEnabled]);

  useEffect(() => {
    localStorage.setItem('autoStartScanner', JSON.stringify(autoStartScanner));
  }, [autoStartScanner]);

  useEffect(() => {
    localStorage.setItem('autoRefreshEnabled', JSON.stringify(autoRefreshEnabled));
  }, [autoRefreshEnabled]);

  // Fetch trading pairs for manual signal generator
  useEffect(() => {
    const fetchPairs = async () => {
      try {
        const response = await axios.get(`${API}/pairs`);
        if (response.data.success) {
          setManualPairs(response.data.pairs);
        }
      } catch (error) {
        console.error('Error fetching pairs:', error);
      }
    };
    fetchPairs();
  }, []);

  // Check authentication on load
  useEffect(() => {
    const checkAuth = async () => {
      setIsCheckingAuth(true);
      
      // Check if admin is logged in
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        try {
          const response = await fetch(`${API}/admin/verify?token=${adminToken}`);
          const data = await response.json();
          if (data.valid) {
            setIsAdmin(true);
            setIsCheckingAuth(false);
            return;
          } else {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
          }
        } catch (err) {
          console.log('Admin token invalid');
        }
      }
      
      // Check if guest is logged in
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken) {
        try {
          const response = await fetch(`${API}/guest/verify?token=${guestToken}`);
          const data = await response.json();
          if (data.valid && data.user) {
            setGuestUser(data.user);
          } else {
            localStorage.removeItem('guestToken');
            localStorage.removeItem('guestUser');
          }
        } catch (err) {
          console.log('Guest token invalid');
        }
      }
      
      setIsCheckingAuth(false);
    };
    
    checkAuth();
  }, []);

  // Heartbeat for online tracking (every 2 minutes)
  useEffect(() => {
    const sendHeartbeat = async () => {
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken) {
        try {
          await fetch(`${API}/guest/heartbeat?token=${guestToken}`, { method: 'POST' });
        } catch (err) {
          // Ignore heartbeat errors
        }
      }
    };

    // Send initial heartbeat
    sendHeartbeat();
    
    // Set up interval
    const interval = setInterval(sendHeartbeat, 120000); // 2 minutes
    
    return () => clearInterval(interval);
  }, []);

  // Guest login success handler
  const handleGuestLoginSuccess = (user) => {
    setGuestUser(user);
    toast.success(`Welcome, ${user.name}!`);
  };

  // Logout handler
  const handleLogout = async () => {
    const guestToken = localStorage.getItem('guestToken');
    if (guestToken) {
      try {
        await fetch(`${API}/guest/logout?token=${guestToken}`, { method: 'POST' });
      } catch (err) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('guestToken');
    localStorage.removeItem('guestUser');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setGuestUser(null);
    setIsAdmin(false);
    toast.success('Logged out successfully');
  };

  // Check notification permission on load
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    connectToPocketOption();
    checkScannerStatus();
    fetch24hStats(); // Fetch 24h stats on load
    fetchPollStats(); // Fetch poll stats on load
    
    // Auto-start scanner if enabled
    const attemptAutoStart = async () => {
      if (!autoStartScanner) {
        console.log('⏸️ Auto-start disabled - skipping');
        return;
      }
      
      // Wait for backend to be ready
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      try {
        // Check current scanner status first
        const statusResponse = await axios.get(`${API}/scanner/status`);
        const isRunning = statusResponse.data.running;
        
        if (isRunning) {
          console.log('✅ Scanner already running - skipping auto-start');
          setScannerRunning(true);
          return;
        }
        
        console.log('🤖 Auto-start enabled - starting scanner...');
        await startScanner();
        console.log('✅ Scanner auto-started successfully');
      } catch (error) {
        console.error('❌ Auto-start failed:', error);
      }
    };
    
    attemptAutoStart();
    
    // ULTRA-AGGRESSIVE: Keep-alive ping every 2 minutes
    const keepAliveInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/keep-alive`, { timeout: 5000 });
        console.log('💓 Keep-alive ping successful');
        
        // If backend was sleeping and just woke up, refresh scanner status
        if (scannerRunning) {
          fetchScannerStatus();
        }
      } catch (error) {
        console.error('⚠️ Backend may be sleeping - attempting wake-up...');
        
        // Try multiple wake-up attempts
        for (let i = 0; i < 3; i++) {
          try {
            await axios.get(`${API}/keep-alive`, { timeout: 10000 });
            console.log('✅ Backend woken up successfully!');
            toast.info('Backend reconnected and ready');
            
            // If auto-start enabled and scanner not running, check status first then restart if needed
            if (autoStartScanner) {
              try {
                const statusResponse = await axios.get(`${API}/scanner/status`);
                if (!statusResponse.data.running) {
                  console.log('🤖 Backend reconnected - auto-restarting scanner...');
                  await startScanner();
                } else {
                  console.log('✅ Scanner still running after reconnect');
                }
              } catch (err) {
                console.error('Could not check scanner status:', err);
              }
            }
            break;
          } catch (retryError) {
            console.error(`Wake-up attempt ${i + 1} failed`);
            if (i === 2) {
              toast.error('Backend is not responding. Please refresh the page.');
            }
          }
        }
      }
    }, 120000); // Every 2 minutes (120000ms) - ULTRA-AGGRESSIVE
    
    // Additional aggressive ping every 30 seconds when scanner is running
    const aggressivePingInterval = setInterval(async () => {
      if (scannerRunning) {
        try {
          await axios.get(`${API}/scanner/status`, { timeout: 3000 });
        } catch (error) {
          console.error('⚠️ Scanner status check failed');
        }
      }
    }, 30000); // Every 30 seconds when scanner active
    
    return () => {
      clearInterval(keepAliveInterval);
      clearInterval(aggressivePingInterval);
    };
  }, [autoStartScanner]);

  // Auto-refresh system - Refreshes page every 10 minutes to keep everything fresh
  useEffect(() => {
    if (!autoRefreshEnabled) {
      setNextRefreshTime(null);
      return;
    }

    const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes in milliseconds
    const targetTime = Date.now() + REFRESH_INTERVAL;
    
    console.log('🔄 Auto-refresh enabled - Page will refresh in 10 minutes');

    const refreshTimer = setTimeout(() => {
      console.log('🔄 Auto-refresh triggered - Reloading page...');
      toast.info('Auto-refreshing page to keep system active...', { duration: 2000 });
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }, REFRESH_INTERVAL);

    // Update countdown every second by recalculating
    const countdownInterval = setInterval(() => {
      const now = Date.now();
      if (now < targetTime) {
        setNextRefreshTime(targetTime); // Force re-render to update countdown
      }
    }, 1000);

    // Set initial time
    setNextRefreshTime(targetTime);

    return () => {
      clearTimeout(refreshTimer);
      clearInterval(countdownInterval);
    };
  }, [autoRefreshEnabled]);

  useEffect(() => {
    let interval;
    if (scannerRunning) {
      interval = setInterval(() => {
        fetchRecentSignals();
        fetchScannerStatus();
        fetch24hStats(); // Update 24h stats while running
        fetchPollStats(); // Update poll stats while running
      }, 5000); // Update every 5 seconds
    } else {
      // Fetch 24h stats even when not running
      const statsInterval = setInterval(() => {
        fetch24hStats();
        fetchPollStats();
      }, 30000); // Update every 30 seconds
      return () => clearInterval(statsInterval);
    }
    return () => clearInterval(interval);
  }, [scannerRunning]);

  useEffect(() => {
    let interval;
    if (botRunning) {
      interval = setInterval(() => {
        fetchSignals();
        fetchBotStatus();
      }, 5000); // Update every 5 seconds
    }
    return () => clearInterval(interval);
  }, [botRunning]);

  const connectToPocketOption = async () => {
    setLoading(true);
    try {
      const ssid = process.env.REACT_APP_POCKET_OPTION_SSID;
      if (!ssid) {
        // Skip connection attempt if SSID not configured - this is optional for scanner mode
        console.log('ℹ️ Pocket Option SSID not configured - operating in scanner-only mode');
        setLoading(false);
        return;
      }
      const response = await axios.post(`${API}/connect`, {
        ssid: ssid,
        account_type: accountType
      });

      if (response.data.success) {
        setConnected(true);
        setBalance(response.data.balance.balance);
        toast.success('Connected to Pocket Option');
        await fetchAssets();
      } else {
        console.log('ℹ️ Connection not established - operating in scanner-only mode');
      }
    } catch (error) {
      // Silently handle connection errors - scanner works independently
      console.log('ℹ️ Connection not available - operating in scanner-only mode');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      const assetsList = response.data.assets || [];
      setAssets(assetsList);
      
      // Default assets for OTC, stocks, and commodities
      const defaultAssets = [
        { symbol: 'EURUSD_OTC', name: 'EUR/USD OTC', type: 'forex' },
        { symbol: 'GBPUSD_OTC', name: 'GBP/USD OTC', type: 'forex' },
        { symbol: 'USDJPY_OTC', name: 'USD/JPY OTC', type: 'forex' },
        { symbol: 'GOLD_OTC', name: 'Gold OTC', type: 'commodity' },
        { symbol: 'SILVER_OTC', name: 'Silver OTC', type: 'commodity' },
        { symbol: 'OIL_OTC', name: 'Oil OTC', type: 'commodity' },
        { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
        { symbol: 'GOOGL', name: 'Google', type: 'stock' },
        { symbol: 'TSLA', name: 'Tesla', type: 'stock' }
      ];
      
      if (assetsList.length === 0) {
        setAssets(defaultAssets);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      // Set default assets on error
      setAssets([
        { symbol: 'EURUSD_OTC', name: 'EUR/USD OTC', type: 'forex' },
        { symbol: 'GBPUSD_OTC', name: 'GBP/USD OTC', type: 'forex' },
        { symbol: 'GOLD_OTC', name: 'Gold OTC', type: 'commodity' },
        { symbol: 'OIL_OTC', name: 'Oil OTC', type: 'commodity' },
        { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' }
      ]);
    }
  };

  const switchAccount = async (type) => {
    try {
      const response = await axios.post(`${API}/account/switch?account_type=${type}`);
      if (response.data.success) {
        setAccountType(type);
        setBalance(response.data.balance.balance);
        toast.success(`Switched to ${type} account`);
      }
    } catch (error) {
      toast.error('Failed to switch account');
    }
  };

  const startBot = async () => {
    try {
      const response = await axios.post(`${API}/bot/start`, {
        asset: selectedAsset,
        amount: tradeAmount,
        mode: botMode
      });

      if (response.data.success) {
        setBotRunning(true);
        toast.success(`Bot started in ${botMode} mode`);
      }
    } catch (error) {
      toast.error('Failed to start bot');
    }
  };

  const stopBot = async () => {
    try {
      const response = await axios.post(`${API}/bot/stop`);
      if (response.data.success) {
        setBotRunning(false);
        toast.success('Bot stopped');
      }
    } catch (error) {
      toast.error('Failed to stop bot');
    }
  };

  const fetchSignals = async () => {
    try {
      const response = await axios.get(`${API}/signals`);
      setSignals(response.data);
    } catch (error) {
      console.error('Error fetching signals:', error);
    }
  };

  const fetchBotStatus = async () => {
    try {
      const response = await axios.get(`${API}/bot/status`);
      setBotStatus(response.data);
    } catch (error) {
      console.error('Error fetching bot status:', error);
    }
  };

  const executeTrade = async (direction) => {
    try {
      const response = await axios.post(`${API}/trade`, {
        asset: selectedAsset,
        direction: direction,
        amount: tradeAmount
      });

      if (response.data.success) {
        toast.success(`${direction.toUpperCase()} trade executed`);
        await fetchBalance();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Trade execution failed');
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/balance`);
      setBalance(response.data.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const startScanner = async () => {
    try {
      setLoading(true);
      // Use centralized config with fallback
      const telegramToken = config.getTelegramBotToken();
      const telegramChatId = config.getTelegramChatId();
      
      const response = await axios.post(`${API}/scanner/start`, {
        telegram_bot_token: telegramToken,
        telegram_chat_id: telegramChatId,
        telegram_channel_id: telegramChannelId || undefined,
        timeframe: selectedTimeframe,
        send_to_bot: sendToBot,
        send_to_channel: sendToChannel
      });

      if (response.data.success) {
        setScannerRunning(true);
        toast.success('Multi-asset scanner started! You will receive signals on Telegram.');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to start scanner');
    } finally {
      setLoading(false);
    }
  };

  const updateTelegramSettings = async () => {
    try {
      const response = await axios.post(`${API}/scanner/telegram-settings`, {
        send_to_bot: sendToBot,
        send_to_channel: sendToChannel,
        telegram_channel_id: telegramChannelId || null
      });

      if (response.data.success) {
        toast.success('Telegram settings updated!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const updateReportingMode = async (autoResult, autoMartingale) => {
    try {
      const response = await axios.post(`${API}/scanner/reporting-mode`, {
        auto_report_result: autoResult,
        auto_report_martingale: autoMartingale
      });

      if (response.data.success) {
        toast.success('Reporting mode updated!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to update reporting mode');
    }
  };

  const submitManualResult = async (result) => {
    if (!window.confirm(`Are you sure you want to report this trade as ${result.toUpperCase()}?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/scanner/manual-result`, {
        result: result
      });

      if (response.data.success) {
        toast.success(`Result '${result.toUpperCase()}' reported successfully!`);
        setPendingManualResult(false);
        checkScannerStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to submit result');
    } finally {
      setLoading(false);
    }
  };

  const stopScanner = async () => {
    try {
      const response = await axios.post(`${API}/scanner/stop`);
      if (response.data.success) {
        setScannerRunning(false);
        
        // IMPORTANT: Disable auto-start when user manually stops scanner
        setAutoStartScanner(false);
        toast.success('Scanner stopped (Auto-start disabled)');
        console.log('🛑 Scanner manually stopped - Auto-start disabled');
      }
    } catch (error) {
      toast.error('Failed to stop scanner');
    }
  };

  // Generate manual signal for selected pair - works independently, no popup
  const generateManualSignal = async () => {
    setGeneratingSignal(true);
    setLastManualSignalResult(null);
    
    try {
      const response = await axios.post(`${API}/scanner/manual-signal`, {
        symbol: selectedManualPair
      });
      
      if (response.data.success) {
        if (response.data.signal_found) {
          // Show success toast with signal info
          const signalEmoji = response.data.signal === 'BUY' ? '🟢' : '🔴';
          toast.success(`${signalEmoji} ${response.data.signal} - ${response.data.accuracy}% accuracy`);
          
          // Store full result for display - NO POPUP
          setLastManualSignalResult({
            type: 'success',
            signal: response.data.signal,
            asset_name: response.data.asset_name,
            symbol: response.data.symbol,
            accuracy: response.data.accuracy,
            expiry: response.data.expiry,
            reason: response.data.reason,
            indicator_source: response.data.indicator_source,
            payout: response.data.payout,
            execute_time_est: response.data.execute_time_est,
            execute_in_seconds: response.data.execute_in_seconds,
            timeframe: response.data.timeframe,
            message: response.data.message
          });
        } else {
          toast.info(`No signal for ${response.data.asset_name || selectedManualPair}`);
          setLastManualSignalResult({
            type: 'no_signal',
            asset_name: response.data.asset_name,
            symbol: response.data.symbol,
            message: response.data.message,
            reason: response.data.reason,
            indicators: response.data.indicators
          });
        }
      } else {
        toast.error(response.data.message || 'Failed to generate signal');
        setLastManualSignalResult({
          type: 'error',
          message: response.data.message
        });
      }
    } catch (error) {
      console.error('Error generating manual signal:', error);
      toast.error('Failed to generate signal');
      setLastManualSignalResult({
        type: 'error',
        message: error.message
      });
    } finally {
      setGeneratingSignal(false);
    }
  };

  const checkScannerStatus = async () => {
    try {
      const response = await axios.get(`${API}/scanner/status`);
      if (response.data.running) {
        setScannerRunning(true);
        setScannerStatus(response.data);
      }
    } catch (error) {
      console.error('Error checking scanner status:', error);
    }
  };

  const fetchScannerStatus = async () => {
    try {
      const response = await axios.get(`${API}/scanner/status`);
      const newStatus = response.data;
      
      // Check if there's a new active trade (signal)
      if (newStatus.active_trade && newStatus.active_trade_details) {
        const signalId = `${newStatus.active_trade}-${newStatus.active_trade_signal}-${newStatus.active_trade_details?.timestamp}`;
        
        // Only show popup if this is a NEW signal we haven't seen
        if (signalId !== lastSignalId) {
          setLastSignalId(signalId);
          
          // Show dashboard popup notification if enabled
          if (dashboardNotificationsEnabled) {
            setCurrentSignalDetails(newStatus.active_trade_details);
            setShowSignalPopup(true);
          }
          
          // Also show browser notification
          const signal = newStatus.active_trade_signal || 'SIGNAL';
          const asset = newStatus.active_trade;
          showNotification(
            `🔔 New ${signal} Signal!`,
            `${asset} - Check the app for details`
          );
        } else {
          // Update countdown for existing signal (only if popup is enabled)
          if (dashboardNotificationsEnabled && currentSignalDetails && newStatus.active_trade_details) {
            setCurrentSignalDetails(prev => ({
              ...prev,
              execute_countdown: newStatus.active_trade_details.execute_countdown
            }));
          }
        }
      } else {
        // No active trade - close popup if open
        if (showSignalPopup && !newStatus.trade_in_progress) {
          setShowSignalPopup(false);
          setCurrentSignalDetails(null);
        }
      }
      
      // If notifications are disabled, close any open popup immediately
      if (!dashboardNotificationsEnabled && showSignalPopup) {
        setShowSignalPopup(false);
      }
      
      setScannerStatus(newStatus);
      
      // Update reporting mode states
      if (newStatus.auto_report_result !== undefined) {
        setAutoReportResult(newStatus.auto_report_result);
      }
      if (newStatus.auto_report_martingale !== undefined) {
        setAutoReportMartingale(newStatus.auto_report_martingale);
      }
      if (newStatus.pending_manual_result !== undefined) {
        setPendingManualResult(newStatus.pending_manual_result);
      }
    } catch (error) {
      console.error('Error fetching scanner status:', error);
    }
  };

  const fetchRecentSignals = async () => {
    try {
      const response = await axios.get(`${API}/signals/recent?limit=10`);
      setRecentSignals(response.data.signals || []);
    } catch (error) {
      console.error('Error fetching recent signals:', error);
    }
  };

  const fetch24hStats = async () => {
    try {
      const response = await axios.get(`${API}/trades/stats-24h`);
      setStats24h(response.data);
    } catch (error) {
      console.error('Error fetching 24h stats:', error);
    }
  };

  const fetchPollStats = async () => {
    try {
      const response = await axios.get(`${API}/polls/stats`);
      setPollStats(response.data);
    } catch (error) {
      console.error('Error fetching poll stats:', error);
    }
  };

  const getTimeUntilRefresh = (targetTime) => {
    if (!targetTime) return '--:--';
    
    const now = Date.now();
    const diff = targetTime - now;
    
    if (diff <= 0) return '00:00';
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        toast.success('Notifications enabled successfully!');
        return true;
      } else {
        toast.error('Notification permission denied');
        return false;
      }
    } else {
      toast.error('Notifications are blocked. Please enable them in browser settings.');
      return false;
    }
  };

  const showNotification = (title, body) => {
    if (pushNotificationsEnabled && Notification.permission === 'granted') {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        // Use service worker for background notifications
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, {
            body: body,
            icon: '/icon-192.png',
            badge: '/icon-192.png',
            vibrate: [200, 100, 200],
            tag: 'trading-signal',
            requireInteraction: true,
            actions: [
              { action: 'open', title: 'Open' },
              { action: 'dismiss', title: 'Dismiss' }
            ]
          });
        });
      } else {
        // Fallback to regular notification
        new Notification(title, {
          body: body,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          vibrate: [200, 100, 200]
        });
      }
    }
  };

  const handleTogglePushNotifications = async (enabled) => {
    if (enabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        setPushNotificationsEnabled(true);
        toast.success('Push notifications enabled! You will be notified of new signals even when app is in background.');
      } else {
        setPushNotificationsEnabled(false);
      }
    } else {
      setPushNotificationsEnabled(false);
      toast.info('Push notifications disabled');
    }
  };

  const reportResult = async (result) => {
    try {
      const response = await axios.post(`${API}/scanner/report-result`, {
        result: result
      });

      if (response.data.success) {
        if (result === 'win') {
          toast.success('Trade WIN reported! 🎉');
        } else {
          toast.error('Trade LOSS reported');
        }
        
        if (response.data.blocked) {
          toast.error('🛑 3 consecutive losses! Trading STOPPED');
        } else if (response.data.consecutive_losses === 2) {
          toast.warning('⚠️ Warning: 2 consecutive losses');
        }
        
        // Refresh scanner status
        await fetchScannerStatus();
      }
    } catch (error) {
      toast.error('Failed to report result');
    }
  };

  const resetLosses = async () => {
    try {
      const response = await axios.post(`${API}/scanner/reset-losses`);
      if (response.data.success) {
        toast.success('Consecutive losses reset! Trading resumed.');
        await fetchScannerStatus();
      }
    } catch (error) {
      toast.error('Failed to reset losses');
    }
  };

  const sendRiskReminder = async () => {
    try {
      const response = await axios.post(`${API}/scanner/send-risk-reminder`);
      if (response.data.success) {
        const destinations = [];
        if (response.data.sent_to_bot) destinations.push('Bot');
        if (response.data.sent_to_channel) destinations.push('Channel');
        
        toast.success(`Risk reminder sent to ${destinations.join(' and ')}!`);
      } else {
        toast.error(response.data.message || 'Failed to send risk reminder');
      }
    } catch (error) {
      console.error('Error sending risk reminder:', error);
      toast.error('Failed to send risk reminder');
    }
  };

  const sendTradingWarning = async () => {
    try {
      const response = await axios.post(`${API}/scanner/send-trading-warning`);
      if (response.data.success) {
        const destinations = [];
        if (response.data.sent_to_bot) destinations.push('Bot');
        if (response.data.sent_to_channel) destinations.push('Channel');
        
        toast.success(`Trading warning sent to ${destinations.join(' and ')}!`);
      } else {
        toast.error(response.data.message || 'Failed to send trading warning');
      }
    } catch (error) {
      console.error('Error sending trading warning:', error);
      toast.error('Failed to send trading warning');
    }
  };

  const sendCelebration = async () => {
    try {
      const response = await axios.post(`${API}/scanner/send-celebration`);
      if (response.data.success) {
        const destinations = [];
        if (response.data.sent_to_bot) destinations.push('Bot');
        if (response.data.sent_to_channel) destinations.push('Channel');
        
        toast.success(`🎉 Celebration sent to ${destinations.join(' and ')}!`);
      } else {
        toast.error(response.data.message || 'Failed to send celebration');
      }
    } catch (error) {
      console.error('Error sending celebration:', error);
      toast.error('Failed to send celebration');
    }
  };

  return (
    <div className="dashboard-container" data-testid="trading-dashboard">
      <Toaster position="top-right" richColors />
      <UpdatePrompt />
      <InstallPrompt />
      
      {/* Signal Notification Popup */}
      {showSignalPopup && currentSignalDetails && (
        <SignalNotificationPopup
          signalDetails={currentSignalDetails}
          onClose={() => setShowSignalPopup(false)}
          onDismiss={() => {
            setShowSignalPopup(false);
            setCurrentSignalDetails(null);
          }}
        />
      )}
      
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <Activity className="logo-icon" />
            <h1 className="logo-text">Pocket Option Bot</h1>
          </div>
          
          <div className="flex items-center gap-4">
            {/* User Info / Auth Section */}
            <div className="flex items-center gap-2">
              {isAdmin ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                    <Shield className="w-3 h-3 mr-1" />
                    Admin
                  </Badge>
                  <a href="/admin/dashboard" className="text-xs text-purple-400 hover:text-purple-300">
                    Dashboard
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                  >
                    <LogOut className="w-3 h-3" />
                  </Button>
                </div>
              ) : guestUser ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">
                    <User className="w-3 h-3 mr-1" />
                    {guestUser.name}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                  >
                    <LogOut className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAuthModal(true)}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 h-7 px-2"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    <span className="text-xs">Guest Login</span>
                  </Button>
                  <a href="/admin" className="text-xs text-gray-400 hover:text-gray-300">
                    Admin
                  </a>
                </div>
              )}
            </div>
            
            <ConnectionStatus 
              connected={connected}
              accountType={accountType}
              balance={balance}
              onSwitchAccount={switchAccount}
              data-testid="connection-status"
            />
          </div>
        </div>
      </header>

      <div className="main-content">
        {/* Multi-Asset Scanner */}
        <Card className="scanner-panel" data-testid="scanner-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Multi-Asset Auto-Scanner
            </CardTitle>
            <CardDescription>
              Automatically scans all OTC pairs, commodities, and stocks for trading signals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="scanner-controls">
              {/* Timeframe Selector */}
              {/* Reporting Mode Settings */}
              <div className="space-y-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg border-2 border-blue-200">
                <h3 className="text-sm font-semibold mb-2">📊 Result Reporting Mode</h3>
                
                {/* Auto Report Result Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Auto Report Trade Result</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${autoReportResult ? 'text-green-600' : 'text-red-600'}`}>
                      {autoReportResult ? 'AUTO' : 'MANUAL'}
                    </span>
                    <Switch
                      checked={autoReportResult}
                      onCheckedChange={(checked) => {
                        setAutoReportResult(checked);
                        if (scannerRunning) updateReportingMode(checked, autoReportMartingale);
                      }}
                      className={`${autoReportResult ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-orange-500'}`}
                      data-testid="toggle-auto-result"
                    />
                  </div>
                </div>

                {/* Auto Report Martingale Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Auto Report Martingale</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${autoReportMartingale ? 'text-green-600' : 'text-red-600'}`}>
                      {autoReportMartingale ? 'AUTO' : 'MANUAL'}
                    </span>
                    <Switch
                      checked={autoReportMartingale}
                      onCheckedChange={(checked) => {
                        setAutoReportMartingale(checked);
                        if (scannerRunning) updateReportingMode(autoReportResult, checked);
                      }}
                      className={`${autoReportMartingale ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-orange-500'}`}
                      data-testid="toggle-auto-martingale"
                    />
                  </div>
                </div>

                {/* Manual Result Buttons (Show when pending) */}
                {pendingManualResult && (
                  <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg border-2 border-yellow-400">
                    <p className="text-sm font-semibold mb-2 text-center">⏸️ Manual Result Required</p>
                    <div className="flex gap-2 justify-center">
                      <Button
                        onClick={() => submitManualResult('win')}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        ✅ WIN
                      </Button>
                      <Button
                        onClick={() => submitManualResult('loss')}
                        disabled={loading}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        ❌ LOSS
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Active Trade Manual Override - Always Available */}
              {scannerRunning && scannerStatus?.trade_in_progress && scannerStatus?.active_trade && (
                <div className="space-y-3 mb-6 p-4 bg-purple-50 dark:bg-purple-900 rounded-lg border-2 border-purple-300">
                  <h3 className="text-sm font-semibold mb-2">🎯 Active Trade - Manual Report</h3>
                  
                  <div className="text-sm space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">📊 Current Trade:</p>
                      <span className="text-blue-600 dark:text-blue-400 font-semibold">{scannerStatus.active_trade}</span>
                    </div>
                    
                    {/* Signal Type Display */}
                    {scannerStatus.active_trade_signal && (
                      <div className="flex items-center gap-2">
                        <p className="font-medium">📈 Signal:</p>
                        <span className={`px-3 py-1 rounded-full font-bold text-sm ${
                          scannerStatus.active_trade_signal === 'BUY' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-red-500 text-white'
                        }`}>
                          {scannerStatus.active_trade_signal === 'BUY' ? '🟢 BUY' : '🔴 SELL'}
                        </span>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-600 dark:text-gray-400">Report your actual trade result manually</p>
                  </div>

                  {/* Countdown Timer */}
                  {scannerStatus.manual_report_seconds_remaining !== null && scannerStatus.manual_report_seconds_remaining !== undefined && (
                    <div className="flex items-center justify-center gap-2 py-2">
                      <span className="text-lg font-bold">⏰</span>
                      <span className={`text-2xl font-bold tabular-nums ${
                        scannerStatus.manual_report_seconds_remaining <= 10 
                          ? 'text-red-600 animate-pulse' 
                          : scannerStatus.manual_report_seconds_remaining <= 30 
                            ? 'text-orange-600' 
                            : 'text-blue-600'
                      }`}>
                        {scannerStatus.manual_report_seconds_remaining}s
                      </span>
                    </div>
                  )}
                  
                  {scannerStatus.manual_report_seconds_remaining !== null && scannerStatus.manual_report_seconds_remaining !== undefined && (
                    <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                      {scannerStatus.manual_report_seconds_remaining > 0 
                        ? 'Auto-report will trigger if no manual input' 
                        : 'Auto-reporting...'}
                    </p>
                  )}

                  <div className="flex gap-2 justify-center pt-2">
                    <Button
                      onClick={() => submitManualResult('win')}
                      disabled={loading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3"
                    >
                      ✅ WIN
                    </Button>
                    <Button
                      onClick={() => submitManualResult('loss')}
                      disabled={loading}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3"
                    >
                      ❌ LOSS
                    </Button>
                  </div>

                  <div className="flex gap-2 justify-center pt-2">
                    <Button
                      onClick={() => submitManualResult('asset_not_found')}
                      disabled={loading}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 text-xs"
                    >
                      🚫 ASSET NOT FOUND
                    </Button>
                    <Button
                      onClick={() => submitManualResult('low_payout')}
                      disabled={loading}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 text-xs"
                    >
                      ⚠️ PAYOUT &lt; 70%
                    </Button>
                  </div>

                  <p className="text-xs text-center text-gray-500 pt-2">
                    Click based on your actual trade result in Pocket Option
                  </p>
                </div>
              )}

              {/* Telegram Settings */}
              <div className="space-y-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-2">
                <h3 className="text-sm font-semibold mb-2">📱 Telegram Notifications</h3>
                
                {/* Send to Bot Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Send to Personal Bot</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${sendToBot ? 'text-green-600' : 'text-red-600'}`}>
                      {sendToBot ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={sendToBot}
                      onCheckedChange={(checked) => {
                        setSendToBot(checked);
                        if (scannerRunning) updateTelegramSettings();
                      }}
                      className={`${sendToBot ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-red-500'}`}
                      data-testid="toggle-send-to-bot"
                    />
                  </div>
                </div>

                {/* Dashboard Notifications Toggle - PROMINENT */}
                <div className="bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-400 rounded-lg p-3 -mx-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-base font-bold text-blue-700 dark:text-blue-300">🔔 Dashboard Popup Alert</label>
                      <p className="text-xs text-blue-600 dark:text-blue-400">Show popup + sound when signal detected</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${dashboardNotificationsEnabled ? 'text-green-600' : 'text-red-600'}`}>
                        {dashboardNotificationsEnabled ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={dashboardNotificationsEnabled}
                        onCheckedChange={setDashboardNotificationsEnabled}
                        className={`${dashboardNotificationsEnabled ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-red-500'}`}
                        data-testid="toggle-dashboard-notifications"
                      />
                    </div>
                  </div>
                </div>

                {/* Send to Channel Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Send to Channel</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${sendToChannel ? 'text-green-600' : 'text-red-600'}`}>
                      {sendToChannel ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={sendToChannel}
                      onCheckedChange={(checked) => {
                        setSendToChannel(checked);
                        if (scannerRunning) updateTelegramSettings();
                      }}
                      className={`${sendToChannel ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-red-500'}`}
                      data-testid="toggle-send-to-channel"
                    />
                  </div>
                </div>

                {/* Channel ID Input */}
                {sendToChannel && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Channel Username or ID</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="@yourchannel or -100123456789"
                        value={telegramChannelId}
                        onChange={(e) => setTelegramChannelId(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-700"
                        data-testid="channel-id-input"
                      />
                      {scannerRunning && (
                        <Button
                          onClick={updateTelegramSettings}
                          variant="outline"
                          size="sm"
                          className="whitespace-nowrap"
                        >
                          Save
                        </Button>
                      )}
                    </div>
                    <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                      <p className="font-semibold">⚠️ Important:</p>
                      <p>1. Add your bot as ADMIN to the channel first</p>
                      <p>2. Use @username (public) or -100XXXXX (private)</p>
                      <p>3. Invite links do not work - use actual channel ID</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-Start Scanner */}
              <div className="space-y-4 mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900 dark:to-cyan-900 rounded-lg border-2 border-blue-300">
                <h3 className="text-sm font-semibold mb-2">🤖 Auto-Start Scanner</h3>
                
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium">Auto-Start on Load</label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Scanner starts automatically when page loads or backend reconnects
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${autoStartScanner ? 'text-green-600' : 'text-gray-600'}`}>
                      {autoStartScanner ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={autoStartScanner}
                      onCheckedChange={setAutoStartScanner}
                      className={`${autoStartScanner ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-gray-400'}`}
                      data-testid="toggle-auto-start"
                    />
                  </div>
                </div>

                {autoStartScanner && (
                  <div className="text-xs text-blue-600 bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-200">
                    <p className="font-semibold">✅ Auto-Start Enabled</p>
                    <p>Scanner will start automatically when you open the app. Turn OFF to manual control.</p>
                  </div>
                )}
              </div>

              {/* PWA Push Notifications */}
              <div className="space-y-4 mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900 dark:to-pink-900 rounded-lg border-2 border-purple-300">
                <h3 className="text-sm font-semibold mb-2">🔔 Push Notifications (PWA)</h3>
                
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium">Background Notifications</label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Get notified of new signals even when app is in background
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${pushNotificationsEnabled ? 'text-green-600' : 'text-gray-600'}`}>
                      {pushNotificationsEnabled ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={pushNotificationsEnabled}
                      onCheckedChange={handleTogglePushNotifications}
                      className={`${pushNotificationsEnabled ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-gray-400'}`}
                      data-testid="toggle-push-notifications"
                    />
                  </div>
                </div>

                {notificationPermission === 'denied' && (
                  <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200">
                    <p className="font-semibold">⚠️ Notifications Blocked</p>
                    <p>Please enable notifications in your browser settings to use this feature.</p>
                  </div>
                )}

                {pushNotificationsEnabled && (
                  <div className="text-xs text-purple-600 bg-purple-50 dark:bg-purple-900/20 p-2 rounded border border-purple-200">
                    <p className="font-semibold">✅ Push Notifications Active</p>
                    <p>You will receive alerts for new trading signals even when the app is minimized or in background.</p>
                  </div>
                )}
              </div>

              {/* Auto Page Refresh */}
              <div className="space-y-4 mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 rounded-lg border-2 border-green-300">
                <h3 className="text-sm font-semibold mb-2">🔄 Auto Page Refresh</h3>
                
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium">Refresh Every 10 Minutes</label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Keeps the app fresh and maintains backend connectivity
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${autoRefreshEnabled ? 'text-green-600' : 'text-gray-600'}`}>
                      {autoRefreshEnabled ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={autoRefreshEnabled}
                      onCheckedChange={setAutoRefreshEnabled}
                      className={`${autoRefreshEnabled ? 'bg-green-500 data-[state=checked]:bg-green-500' : 'bg-gray-400'}`}
                      data-testid="toggle-auto-refresh"
                    />
                  </div>
                </div>

                {autoRefreshEnabled && nextRefreshTime && (
                  <div className="text-xs text-green-600 bg-green-50 dark:bg-green-900/20 p-2 rounded border border-green-200">
                    <p className="font-semibold">✅ Auto-Refresh Active</p>
                    <p>Next refresh in: <span className="font-mono font-bold">{getTimeUntilRefresh(nextRefreshTime)}</span></p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      💡 This keeps your browser connection alive and prevents memory buildup
                    </p>
                  </div>
                )}
              </div>

              {!scannerRunning && (
                <div className="timeframe-selector" data-testid="timeframe-selector">
                  <label className="timeframe-label">Chart Timeframe:</label>
                  <div className="timeframe-buttons">
                    {[
                      { value: 15, label: '15S' },
                      { value: 30, label: '30S' },
                      { value: 60, label: '1M' },
                      { value: 300, label: '5M' },
                      { value: 900, label: '15M' },
                      { value: 1800, label: '30M' },
                      { value: 3600, label: '1H' },
                      { value: 14400, label: '4H' }
                    ].map((tf) => (
                      <Button
                        key={tf.value}
                        variant={selectedTimeframe === tf.value ? 'default' : 'outline'}
                        onClick={() => setSelectedTimeframe(tf.value)}
                        data-testid={`timeframe-btn-${tf.label}`}
                      >
                        {tf.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {!scannerRunning ? (
                <Button 
                  onClick={startScanner} 
                  disabled={loading}
                  className="scanner-start-btn"
                  data-testid="start-scanner-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {loading ? 'Starting...' : 'Start Auto-Scanner'}
                </Button>
              ) : (
                <Button 
                  onClick={stopScanner} 
                  variant="destructive" 
                  className="scanner-stop-btn"
                  data-testid="stop-scanner-btn"
                >
                  <Square className="w-4 h-4 mr-2" />
                  Stop Scanner
                </Button>
              )}

              {/* Risk Reminder Button */}
              <Button 
                onClick={sendRiskReminder} 
                variant="outline" 
                className="w-full mt-4 bg-orange-50 dark:bg-orange-900/20 border-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                data-testid="risk-reminder-btn"
              >
                <span className="mr-2">🚨</span>
                Send Risk Reminder
              </Button>

              {/* Trading Warning Button */}
              <Button 
                onClick={sendTradingWarning} 
                variant="outline" 
                className="w-full mt-2 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                data-testid="trading-warning-btn"
              >
                <span className="mr-2">📌</span>
                Send Trading Warning
              </Button>

              {/* Celebration Button */}
              <Button 
                onClick={sendCelebration} 
                variant="outline" 
                className="w-full mt-2 bg-green-50 dark:bg-green-900/20 border-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-400"
                data-testid="celebration-btn"
              >
                <span className="mr-2">🎉</span>
                Vibing
              </Button>

              {scannerRunning && scannerStatus && (
                <>
                  {scannerStatus.trade_in_progress && (
                    <div className="active-trade-alert" data-testid="active-trade-alert">
                      <Activity className="w-5 h-5 animate-pulse text-green-500" />
                      <div>
                        <p className="font-semibold">Active Trade in Progress</p>
                        <p className="text-sm text-muted-foreground">
                          Tracking: {scannerStatus.active_trade} (Martingale enabled)
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="scanner-stats" data-testid="scanner-stats">
                    <div className="stat">
                      <span className="stat-label">Timeframe:</span>
                      <span className="stat-value text-blue-500">{scannerStatus.timeframe_name}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Total Trades:</span>
                      <span className="stat-value">{scannerStatus.total_trades || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Wins/Losses:</span>
                      <span className="stat-value text-green-500">{scannerStatus.wins || 0}</span>
                      <span className="stat-value text-red-500">/{scannerStatus.losses || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Consecutive Losses:</span>
                      <span className={`stat-value ${scannerStatus.consecutive_losses >= 2 ? 'text-red-500' : ''}`}>
                        {scannerStatus.consecutive_losses || 0}/3
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* 24-Hour Statistics - Always Visible */}
              {stats24h && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900 dark:to-purple-900 rounded-lg border-2 border-blue-300">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    📊 Last 24 Hours Statistics
                    <span className="text-xs text-gray-600 dark:text-gray-400">(Persists across restarts)</span>
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Signals Sent</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats24h.signals_sent_24h || 0}</p>
                    </div>
                    
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Trades</p>
                      <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{stats24h.trades_24h || 0}</p>
                    </div>
                    
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Wins</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats24h.wins_24h || 0}</p>
                    </div>
                    
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Losses</p>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats24h.losses_24h || 0}</p>
                    </div>
                  </div>
                  
                  {stats24h.trades_24h > 0 && (
                    <div className="mt-3 text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Win Rate: <span className={`font-bold ${stats24h.win_rate_24h >= 60 ? 'text-green-600' : 'text-orange-600'}`}>
                          {stats24h.win_rate_24h}%
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Poll Statistics - Audience Feedback */}
              {pollStats && pollStats.total_votes > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900 dark:to-teal-900 rounded-lg border-2 border-green-300">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    📊 Audience Feedback (24h)
                    <span className="text-xs text-gray-600 dark:text-gray-400">(From Telegram Polls)</span>
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Votes</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{pollStats.total_votes || 0}</p>
                    </div>
                    
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Accurate</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{pollStats.votes_accurate || 0}</p>
                    </div>
                    
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Inaccurate</p>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">{pollStats.votes_inaccurate || 0}</p>
                    </div>
                    
                    <div className="stat-card bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Accuracy Rate</p>
                      <p className={`text-2xl font-bold ${pollStats.accuracy_rate >= 60 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                        {pollStats.accuracy_rate || 0}%
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-center">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Based on {pollStats.total_polls || 0} polls sent in the last 24 hours
                    </p>
                  </div>
                </div>
              )}

              {scannerRunning && scannerStatus && scannerStatus.blocked && (
                <div className="blocked-warning" data-testid="blocked-warning">
                  <p className="text-red-500 font-bold">
                    🛑 TRADING BLOCKED - 3 consecutive losses reached!
                  </p>
                  <p className="text-sm text-muted-foreground">
                    No more signals will be sent. Take a break to protect your account.
                  </p>
                  <Button 
                    onClick={resetLosses} 
                    variant="outline"
                    className="mt-2"
                    data-testid="reset-losses-btn"
                  >
                    Reset & Resume Trading
                  </Button>
                </div>
              )}

              {scannerRunning && !scannerStatus?.blocked && (
                <div className="auto-tracking-info" data-testid="auto-tracking-info">
                  <div className="tracking-badge">
                    <Activity className="w-4 h-4" />
                    <span>🤖 Automatic Result Tracking Active</span>
                  </div>
                  <p className="tracking-description">
                    Bot works with ONE trade at a time:
                  </p>
                  <ul className="tracking-features">
                    <li>🎯 ONE signal at a time - no multiple trades</li>
                    <li>✅ Waits for 1st candle to close (60s)</li>
                    <li>⏳ If loss, waits for 2nd candle (Martingale)</li>
                    <li>📊 Reports WIN/LOSS, then sends next signal</li>
                    <li>🔒 Other assets paused until current trade completes</li>
                  </ul>
                </div>
              )}

              {scannerRunning && recentSignals.length > 0 && (
                <div className="recent-signals" data-testid="recent-signals">
                  <h4 className="signals-title">Recent Signals:</h4>
                  <div className="signals-list">
                    {recentSignals.slice(0, 5).map((signal, index) => (
                      <div key={index} className="signal-item" data-testid={`signal-item-${index}`}>
                        <div className="signal-asset">
                          {signal.signal === 'BUY' ? (
                            <TrendingUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-500" />
                          )}
                          <span>{signal.asset_name}</span>
                        </div>
                        <Badge variant={signal.signal === 'BUY' ? 'default' : 'destructive'}>
                          {signal.signal}
                        </Badge>
                        <span className="signal-time">
                          {new Date(signal.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scannerRunning && (
                <div className="telegram-info" data-testid="telegram-info">
                  <p className="text-sm text-muted-foreground">
                    📱 Signals are being sent to your Telegram
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Manual Signal Generator - Select pair and generate signal */}
        <Card className="control-panel" data-testid="manual-signal-generator">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Manual Signal Generator
            </CardTitle>
            <CardDescription>Select a trading pair and generate signal based on indicator logic</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pair Selection - Always visible */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Trading Pair</label>
              <Select value={selectedManualPair} onValueChange={setSelectedManualPair}>
                <SelectTrigger className="w-full bg-white dark:bg-slate-800 border-2 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-white">
                  <SelectValue placeholder="Select a pair..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] bg-white dark:bg-slate-800 border-2 border-blue-300">
                  {/* Group by type */}
                  <div className="px-2 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30">💱 FOREX</div>
                  {manualPairs.filter(p => p.type === 'forex').map((pair) => (
                    <SelectItem key={pair.symbol} value={pair.symbol} className="text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-blue-900/30">
                      {pair.name}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30">💎 CRYPTO</div>
                  {manualPairs.filter(p => p.type === 'crypto').map((pair) => (
                    <SelectItem key={pair.symbol} value={pair.symbol} className="text-gray-900 dark:text-white hover:bg-purple-50 dark:hover:bg-purple-900/30">
                      {pair.name}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30">🥇 COMMODITIES</div>
                  {manualPairs.filter(p => p.type === 'commodity').map((pair) => (
                    <SelectItem key={pair.symbol} value={pair.symbol} className="text-gray-900 dark:text-white hover:bg-yellow-50 dark:hover:bg-yellow-900/30">
                      {pair.name}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs font-semibold text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30">📈 STOCKS</div>
                  {manualPairs.filter(p => p.type === 'stock').map((pair) => (
                    <SelectItem key={pair.symbol} value={pair.symbol} className="text-gray-900 dark:text-white hover:bg-green-50 dark:hover:bg-green-900/30">
                      {pair.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Generate Signal Button - Requires login */}
            {isAuthenticated() ? (
              <Button 
                onClick={generateManualSignal}
                disabled={generatingSignal}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-6 text-lg"
              >
                {generatingSignal ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Activity className="w-5 h-5 mr-2" />
                    🎯 Generate Signal for {selectedManualPair.replace('_OTC', '')}
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-3">
                <Button 
                  onClick={() => setShowAuthModal(true)}
                  className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-6 text-lg"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  🔒 Login to Generate Signal
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Sign up and get approved to access signal generation
                </p>
              </div>
            )}

            {/* Last Signal Result - Only show if authenticated */}
            {isAuthenticated() && lastManualSignalResult && (
                  <div className={`p-4 rounded-lg ${
                    lastManualSignalResult.type === 'success' 
                      ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-400' 
                      : lastManualSignalResult.type === 'no_signal'
                      ? 'bg-gray-50 dark:bg-gray-800 border border-gray-200'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200'
                  }`}>
                    {lastManualSignalResult.type === 'success' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-2xl font-bold">
                            {lastManualSignalResult.signal === 'BUY' ? '🟢' : '🔴'} 
                            <span className={lastManualSignalResult.signal === 'BUY' ? 'text-green-600' : 'text-red-600'}>
                              {lastManualSignalResult.signal}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${lastManualSignalResult.accuracy >= 85 ? 'text-green-600' : lastManualSignalResult.accuracy >= 75 ? 'text-yellow-600' : 'text-gray-600'}`}>
                              {lastManualSignalResult.accuracy}%
                            </div>
                            <div className="text-xs text-muted-foreground">Accuracy</div>
                          </div>
                        </div>
                        
                        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                          <p className="font-semibold text-lg">📊 {lastManualSignalResult.asset_name}</p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <p>⏱️ Expiry: <span className="font-bold">{lastManualSignalResult.expiry}</span></p>
                            <p>💰 Payout: <span className="font-bold">{lastManualSignalResult.payout}%</span></p>
                            <p>⏰ Execute: <span className="font-bold">{lastManualSignalResult.execute_time_est} EST</span></p>
                            <p>📈 TF: <span className="font-bold">{lastManualSignalResult.timeframe}</span></p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">🎯 {lastManualSignalResult.reason}</p>
                        </div>
                      </div>
                    )}
                    {lastManualSignalResult.type === 'no_signal' && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">⚪</span>
                          <span className="text-gray-700 dark:text-gray-300 font-medium">No Signal - {lastManualSignalResult.asset_name}</span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 bg-muted/50 rounded p-2">
                          {lastManualSignalResult.reason}
                        </p>
                      </div>
                    )}
                    {lastManualSignalResult.type === 'error' && (
                      <div className="text-red-700 dark:text-red-400">
                        ❌ {lastManualSignalResult.message}
                      </div>
                    )}
                  </div>
                )}

            {/* Quick Stats - Always visible */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{manualPairs.length}</div>
                <div className="text-xs text-muted-foreground">Available Pairs</div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{scannerStatus?.signals_found || 0}</div>
                <div className="text-xs text-muted-foreground">Auto Signals</div>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Signals and Statistics */}
        <div className="right-panel">
          {botRunning && signals && (
            <SignalPanel signals={signals} data-testid="signal-panel" />
          )}
          
          <TradeStats data-testid="trade-stats" />
        </div>
      </div>

      {/* Trade History */}
      <div className="history-section">
        <TradeHistory data-testid="trade-history" />
      </div>

      {/* Guest Authentication Modal */}
      <GuestAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleGuestLoginSuccess}
      />
    </div>
  );
};

export default Dashboard;
